import { useRef, useState } from "react";
import { css } from "styled-system/css";
import { UploadCloud, X } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import {
  ODONTOGRAM_IMAGE_CATEGORIES,
  ODONTOGRAM_IMAGE_CATEGORY_LABELS,
  type OdontogramImageCategory,
} from "./odontogram-image-categories";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

const labelClass = css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" });

export function OdontogramImageUploadModal({
  open,
  onClose,
  clientId,
  onUploaded,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  onUploaded: () => void;
}) {
  const { refresh } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [category, setCategory] = useState<OdontogramImageCategory | "">("");
  const [notes, setNotes] = useState("");
  const [takenAt, setTakenAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function resetAndClose() {
    setFiles([]);
    setCategory("");
    setNotes("");
    setTakenAt("");
    setError(null);
    setProgress(null);
    onClose();
  }

  function addFiles(list: FileList | null) {
    if (!list) return;
    const accepted = Array.from(list).filter((f) => ACCEPTED_TYPES.includes(f.type));
    if (accepted.length < list.length) {
      setError("Algunos archivos no son JPG, PNG, WebP o PDF y fueron descartados.");
    }
    setFiles((prev) => [...prev, ...accepted]);
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (files.length === 0) {
      setError("Seleccioná al menos un archivo.");
      return;
    }
    if (!category) {
      setError("Elegí una categoría.");
      return;
    }
    setBusy(true);
    setError(null);
    setProgress({ done: 0, total: files.length });

    let failedCount = 0;
    for (let i = 0; i < files.length; i++) {
      const form = new FormData();
      form.set("clientId", clientId);
      form.set("category", category);
      if (notes.trim()) form.set("notes", notes.trim());
      if (takenAt) form.set("takenAt", takenAt);
      form.set("file", files[i]);

      try {
        const r = await fetchWithSessionSync(
          "/api/dashboard/odontogram/images/upload",
          { method: "POST", body: form, credentials: "include" },
          refresh,
        );
        if (!r.ok) failedCount++;
      } catch {
        failedCount++;
      }
      setProgress({ done: i + 1, total: files.length });
    }

    setBusy(false);
    onUploaded();

    if (failedCount === 0) {
      resetAndClose();
    } else {
      setError(`${failedCount} de ${files.length} archivo(s) no se pudieron subir. Las que sí se subieron ya quedaron guardadas.`);
      setFiles([]);
    }
  }

  return (
    <Modal open={open} onClose={busy ? () => {} : resetAndClose} title="Subir imagen">
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        <div>
          <span className={labelClass}>
            Archivos <span className={css({ color: "#ef4444" })}>*</span>
          </span>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className={css({
              display: "flex",
              flexDir: "column",
              alignItems: "center",
              gap: "2",
              w: "100%",
              py: "6",
              rounded: "lg",
              borderWidth: "1px",
              borderStyle: "dashed",
              borderColor: "#cbd5e1",
              bg: "#f8fafc",
              cursor: busy ? "not-allowed" : "pointer",
              color: "#64748b",
              transition: "background-color 120ms ease, border-color 120ms ease",
              _hover: busy ? {} : { bg: "#f1f5f9", borderColor: "#94a3b8" },
            })}
          >
            <UploadCloud size={22} aria-hidden />
            <span className={css({ fontSize: "sm", fontWeight: "medium" })}>Elegir archivos</span>
            <span className={css({ fontSize: "xs", color: "#94a3b8" })}>JPG, PNG, WebP o PDF · podés elegir varios</span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_TYPES.join(",")}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
            className={css({ display: "none" })}
          />

          {files.length > 0 && (
            <ul className={css({ display: "flex", flexDir: "column", gap: "1.5", mt: "3" })}>
              {files.map((f, idx) => (
                <li
                  key={`${f.name}-${idx}`}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "2",
                    px: "3",
                    py: "1.5",
                    rounded: "md",
                    bg: "#f1f5f9",
                    fontSize: "xs",
                    color: "#334155",
                  })}
                >
                  <span className={css({ truncate: true })}>{f.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    disabled={busy}
                    aria-label={`Quitar ${f.name}`}
                    className={css({ bg: "transparent", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", _hover: { color: "#ef4444" } })}
                  >
                    <X size={14} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label className={labelClass} htmlFor="odon-image-category">
            Categoría <span className={css({ color: "#ef4444" })}>*</span>
          </label>
          <Select
            id="odon-image-category"
            value={category}
            onChange={setCategory}
            disabled={busy}
            placeholder="Elegí una categoría…"
            options={ODONTOGRAM_IMAGE_CATEGORIES.map((c) => ({ value: c, label: ODONTOGRAM_IMAGE_CATEGORY_LABELS[c] }))}
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="odon-image-notes">
            Notas
          </label>
          <Textarea
            id="odon-image-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            rows={2}
            placeholder="Observaciones sobre la imagen…"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="odon-image-taken-at">
            Fecha de la imagen
          </label>
          <Input id="odon-image-taken-at" type="date" value={takenAt} onChange={(e) => setTakenAt(e.target.value)} disabled={busy} />
          <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "1" })}>Opcional — útil para radiografías viejas escaneadas hoy.</p>
        </div>

        {progress && busy ? (
          <p className={css({ fontSize: "xs", color: "#64748b" })}>
            Subiendo {progress.done}/{progress.total}…
          </p>
        ) : null}

        {error ? <p className={css({ fontSize: "sm", color: "#ef4444" })}>{error}</p> : null}

        <div className={css({ display: "flex", justifyContent: "flex-end", gap: "2", mt: "1" })}>
          <Button variant="secondary" size="sm" onClick={resetAndClose} disabled={busy}>
            Cancelar
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={busy}>
            {busy ? "Subiendo…" : "Subir"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
